import type { FulfillmentOrderLine } from "../types";
import type { EntryIssueField, EntryParseBaseInput, EntryParseInput, EntryParseResult } from "./confirmationEntryTypes";
import { parseConfirmationDayExpression } from "./confirmationEntryParser";

export const roles = "学生|老师|教师|导游|司机|司陪|成人|儿童";
/** 归一化公式字符，不承担分类业务规则。 */
export const normalized = (text: string) => text.normalize("NFKC").replace(/[xX*]/g, "×").replace(/[—–~～至]/g, "-").trim();
export type Factor = { value: string; unit: string };

/** 将业务原因与修正位置一起交给录入控件，文本日期不误指向顶部范围。 */
export class EntryParseError extends Error {
  constructor(message: string, readonly field: EntryIssueField, readonly origin?: "control" | "text") { super(message); }
}

/** 按现有精度读取公式，新增费用拒绝零数量及无意义因子。 */
export function factors(text: string, labels: [string, string, string] = ["数量", "次数", "单价"], allowZeroQuantity = false): Factor[] {
  const parts = normalized(text).split("×");
  if (parts.length > 3) throw new Error(`公式有${parts.length}项，最多支持${labels.join("×")}三项；请删除多余乘号或拆分费用`);
  const result = parts.map((part, index) => {
    const label = index === parts.length - 1 ? labels[2] : labels[index];
    if (!part.trim() || part.trim() === "待填") throw new Error(`缺少${label}，请在第${index + 1}项填写实际${label}，例如${labels[2] === "每晚单价" ? "1间×1晚×100元" : "2×1×100元"}`);
    const match = part.trim().match(/^(\d+(?:\.\d{1,2})?)\s*(人|间|晚|天|次|场|桌|餐|辆|项|个|整段|元)?$/);
    if (!match) throw new Error(`第${index + 1}项${label}“${part.trim()}”无法识别，请填写非负数字，最多两位小数，并核对单位`);
    return { value: match[1], unit: match[2] || "" };
  });
  if (result.at(-1)!.unit && result.at(-1)!.unit !== "元") throw new Error(`缺少${labels[2]}：最后一项是“${parts.at(-1)!.trim()}”；请在末尾填写${labels[2]}，例如×100元`);
  const zeroIndex = result.slice(0, -1).findIndex((part, index) => Number(part.value) <= 0 && !(index === 0 && allowZeroQuantity));
  if (zeroIndex >= 0) throw new Error(`${labels[zeroIndex]}为0，请填写大于零的实际${labels[zeroIndex]}；免费费用请将单价填写为0`);
  return result;
}
/** 单位用于验证业务含义，不能为了视觉兼容忽略错误单位。 */
export function unit(part: Factor, accepted: string[]) {
  if (part.unit && !accepted.includes(part.unit)) throw new Error(`单位“${part.unit}”不适用，请使用${accepted.join("/")}`);
}
/** 只有权威上下文中的已知人数可以参与省略人数的公式。 */
export function counts(input: EntryParseBaseInput, role: string) {
  const count = input.roleCounts?.[role === "教师" ? "老师" : role];
  if (!Number.isFinite(count) || Number(count) <= 0) throw new Error(`没有可确认的${role || "本项"}人数，请明确输入实际人数`);
  return String(count);
}
/** 校验显式日次与真实日期映射，日期来源和范围由所属分类决定。 */
export function parseEntryDates(routeDays: EntryParseInput["routeDays"], range: string, label: "住宿" | "服务", origin: "control" | "text"): Partial<FulfillmentOrderLine> {
  const day = parseConfirmationDayExpression(range);
  if (!day) throw new EntryParseError(!range.trim() ? `尚未选择${label}日期，请在上方${label}范围选择实际出行日` : `日期范围“${range}”无法识别，请${origin === "text" ? "修改行首日期" : "填写范围"}为1或1-3等出行日`, "range", origin);
  const knownDays = [...new Set(routeDays.map((item) => item.dayNo))].sort((a, b) => a - b);
  if (!knownDays.length) throw new EntryParseError("当前行程没有出行日，请先到行程中补齐服务日期后再录入", "range", origin);
  const outsideDays = day.numbers.filter((number) => !knownDays.includes(number));
  if (outsideDays.length) {
    const actualRange = knownDays.length > 1 && knownDays.at(-1)! - knownDays[0] + 1 === knownDays.length ? `D${knownDays[0]}-D${knownDays.at(-1)}` : knownDays.map((number) => `D${number}`).join("、");
    throw new EntryParseError(`日期范围“${range}”超出当前行程${actualRange}（共${knownDays.length}天），${outsideDays.map((number) => `D${number}`).join("、")}不存在；请${origin === "text" ? "修改文本中的日期范围，录入以文本日期为准" : "核对实际出行日"}`, "range", origin);
  }
  const dates = day.numbers.map((number) => routeDays.find((item) => item.dayNo === number)?.date);
  const missingDates = day.numbers.filter((_, index) => { const date = dates[index]; return !date || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(`${date}T00:00:00Z`)) || new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) !== date; });
  if (missingDates.length) throw new EntryParseError(`${missingDates.map((number) => `D${number}`).join("、")}缺少有效的真实日期，请先到行程中补齐对应日期后再录入`, "range", origin);
  return { dayExpression: day.expression, dayNumbers: day.numbers, serviceDates: dates as string[], serviceDateFrom: dates[0], serviceDateTo: dates.at(-1), dateMappingStatus: "mapped" };
}
/** 机械收集原文位置、单项解析结果与错误，不决定分类计价规则。 */
export function parseEntrySegments(text: string, parseSegment: (raw: string) => Partial<FulfillmentOrderLine>[], notices: (patch: Partial<FulfillmentOrderLine>) => string[]): EntryParseResult {
  const result: EntryParseResult = { items: [], errors: [] };
  for (const source of text.matchAll(/[^\n;；]+/g)) {
    const raw = source[0]; if (!raw.trim()) continue;
    const location = { sourceStart: source.index, sourceEnd: source.index + raw.length, raw };
    try {
      parseSegment(raw).forEach((patch, index) => {
        const free = Number(patch.amount) === 0;
        patch.remark = `快速录入：${raw}${free ? " 免费" : ""}`;
        result.items.push({
          ...location, key: `${source.index}-${index}`, title: patch.itemNameSnapshot || "费用", patch,
          formula: `${patch.quantity}${patch.unit} × ${patch.billingCount} × ${patch.unitPriceAmount} = ${patch.amount}`,
          notices: notices(patch)
        });
      });
    } catch (error) { result.errors.push({ ...location, message: error instanceof Error ? error.message : "无法识别本段费用，请核对费用名称与数量×次数×单价", field: error instanceof EntryParseError ? error.field : "formula", ...(error instanceof EntryParseError && error.origin ? { origin: error.origin } : {}) }); }
  }
  return result;
}
